import {useTranslation} from '@/translations/i18n'
import {Helmet} from 'react-helmet'
import {PageCardWrapper, PageContent, PageHeading} from '@/components/ui/page-atoms/PageAtoms'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {ButtonLink} from '@/components/ui/button-link/ButtonLink'
import {Button} from '@/components/ui/button/Button'
import {Divider} from '@/components/ui/divider/Divider'
import {ScrollArea} from '@/components/ui/scroll-area/ScrollArea'
import {Scrollbar} from '@/components/ui/scrollbar/Scrollbar'
import {useRef, useState} from 'react'
import {UnsavedBlocker} from '@/components/commons/unsaved-blocker/UnsavedBlocker'
import {FormProvider, useForm, useWatch} from 'react-hook-form'
import {zodResolver} from '@hookform/resolvers/zod'
import {PatientPersonalDataForm} from '@/features/patients/components/patient-personal-data-form/PatientPersonalDataForm'
import {
    FormGridSection,
    FormGridSectionItem,
    FormGridSectionTitle,
    FormGridTitle
} from '@/components/ui/form-atoms/FormAtoms'
import {PatientResidenceForm} from '@/features/patients/components/patient-residence-form/PatientResidenceForm'
import {PatientDomicileForm} from '@/features/patients/components/patient-domicile-form/PatientDomicileForm'
import {PatientContactsFields} from '@/features/patients/components/patient-contacts-fields/PatientContactsFields'
import {PatientMedicalInfoFields} from '@/features/patients/components/patient-medical-info-fields/PatientMedicalInfoFields'
import {PatientPrivacyFields} from '@/features/patients/components/patient-privacy-fields/PatientPrivacyFields'
import {PatientNotesForm} from '@/features/patients/components/patient-notes-form/PatientNotesForm'
import {BannerAlert} from '@/components/commons/banner-alert/BannerAlert'
import {CreatePatientConfirmationModal} from '@/features/patients/components/create-patient-confirmation-modal/CreatePatientConfirmationModal'
import {useDialogStore} from '@ariakit/react/dialog'
import {PatientFormValidationSchema} from '@/features/patients/types'
import {AxiosError} from 'axios'
import {MUTATION_KEYS} from '@/queryClient'
import {useMutationState} from '@tanstack/react-query'
import {usePermissions} from '@/features/auth/store/store'
import {NoPermissionBox} from '@/components/commons/no-permission-box/NoPermissionBox'
import {toast} from '@/utilities/toast/toast'

export const CreatePatient = () => {
    const {t} = useTranslation()
    const permissions = usePermissions()
    const [isErrorBannerVisible, setIsBannerVisible] = useState(false)
    const [isConfirmationModalVisible, setIsConfirmationModalVisible] = useState(false)
    const viewportRef = useRef<HTMLDivElement>(null)
    const errorBannerRef = useRef<HTMLDivElement>(null)

    const confirmationDialogStore = useDialogStore({
        open: isConfirmationModalVisible,
        setOpen: setIsConfirmationModalVisible
    })

    const onErrorConfirmationModal = (errors: AxiosError<{errors: {field: string; rule: string}[]}>) => {
        if (errors?.response?.data.errors) {
            setIsBannerVisible(true)
            errors.response.data.errors.forEach(error => {
                if (error.field == 'residence.postalCode' && error.rule == 'postalCode') {
                    form.setError('residence.postalCode', {
                        message: t('errors:invalid_postal_code'),
                        type: 'custom'
                    })

                    toast.error(t('errors:invalid_postal_code'))
                    setIsConfirmationModalVisible(false)
                }

                if (error.field == 'domicile.postalCode' && error.rule == 'postalCode') {
                    form.setError('domicile.postalCode', {
                        message: t('errors:invalid_postal_code'),
                        type: 'custom'
                    })

                    toast.error(t('errors:invalid_postal_code'))
                    setIsConfirmationModalVisible(false)
                }

                if (error.field == 'taxCode' && error.rule == 'regex') {
                    form.setError('taxCode', {
                        message: t('errors:invalid_tax_code'),
                        type: 'custom'
                    })
                }

                if (error.field == 'taxCode' && error.rule == 'database.exists') {
                    form.setError('taxCode', {
                        message: t('errors:tax_code_already_exists'),
                        type: 'custom'
                    })
                }
            })

            if (viewportRef.current) {
                viewportRef.current.scrollTo({
                    top: 0,
                    behavior: 'smooth'
                })
            }
        }
    }

    // methods
    const form = useForm<PatientFormValidationSchema>({
        mode: 'onBlur',
        resolver: zodResolver(PatientFormValidationSchema),
        defaultValues: {
            isDifferentDomicile: false,
            citizenship: {
                name: 'Italia'
            },
            taxCodeType: 'fiscal_code',
            contacts: [
                {
                    type: 'mobile_phone'
                },
                {
                    type: 'email'
                }
            ]
        }
    })

    // form observers
    const birthDate = useWatch({control: form.control, name: 'birthDate'})

    // actions
    const handleSubmit = form.handleSubmit(
        () => confirmationDialogStore.setOpen(true),
        errors => {
            setIsBannerVisible(true)
            if (viewportRef.current) {
                viewportRef.current.scrollTo({
                    top: 0,
                    behavior: 'smooth'
                })
            }
            console.error(errors)
        }
    )

    // Track all create patient mutation states and extract the status of the most recent mutation
    // This is used to determine if there are unsaved changes that should block navigation
    const createPatientMutationStatuses = useMutationState({
        filters: {
            mutationKey: [MUTATION_KEYS.CREATE_PATIENT]
        },
        select: mutation => mutation.state.status
    })

    const createPatientMutationStatus = createPatientMutationStatuses[createPatientMutationStatuses.length - 1]

    if (!permissions?.patients?.write) {
        return <NoPermissionBox />
    }

    return (
        <PageCardWrapper>
            <Helmet title={t('patients:create_meta_title')} />
            <PageHeading align="center" justify="space-between">
                <h2>{t('commons:add_patient')}</h2>
                <Flexbox gap={2}>
                    <ButtonLink to={'..'} variant="secondary">
                        {t('commons:cancel')}
                    </ButtonLink>
                    <Button onClick={handleSubmit} variant="primaryNeutral">
                        {t('commons:add')}
                    </Button>
                </Flexbox>
            </PageHeading>

            <Divider direction="horizontal" />

            <PageContent padding={0}>
                <ScrollArea style={{height: '100%'}} scrollbar={<Scrollbar />} viewportProps={{ref: viewportRef}}>
                    {isErrorBannerVisible && (
                        <>
                            <BannerAlert ref={errorBannerRef} onClose={() => setIsBannerVisible(false)} />
                            <Divider direction="horizontal" />
                        </>
                    )}
                    <UnsavedBlocker
                        isDirty={
                            (createPatientMutationStatus == 'idle' || !createPatientMutationStatus) &&
                            !!Object.keys(form.formState.dirtyFields).length
                        }
                    >
                        <FormProvider {...form}>
                            <FormGridSection>
                                <FormGridSectionItem>
                                    <FormGridSectionTitle>{t('commons:personal_data')}</FormGridSectionTitle>
                                </FormGridSectionItem>

                                <FormGridSectionItem>
                                    <Flexbox direction="column" gap={8} fullWidth>
                                        <Flexbox direction="column" gap={2.5} fullWidth>
                                            <FormGridTitle>{t('commons:personal_info')}</FormGridTitle>
                                            <PatientPersonalDataForm />
                                        </Flexbox>

                                        <Flexbox direction="column" gap={2.5} fullWidth>
                                            <FormGridTitle>{t('commons:residence')}</FormGridTitle>
                                            <PatientResidenceForm />
                                        </Flexbox>

                                        <Flexbox direction="column" gap={2.5} fullWidth>
                                            <FormGridTitle>{t('commons:domicile')}</FormGridTitle>
                                            <PatientDomicileForm />
                                        </Flexbox>
                                    </Flexbox>
                                </FormGridSectionItem>
                            </FormGridSection>

                            <Divider direction="horizontal" />

                            <FormGridSection>
                                <FormGridSectionItem>
                                    <FormGridSectionTitle>{t('commons:contacts')}</FormGridSectionTitle>
                                </FormGridSectionItem>
                                <FormGridSectionItem>
                                    <PatientContactsFields
                                        title={<FormGridTitle>{t('patients:own_contacts')}</FormGridTitle>}
                                    />
                                </FormGridSectionItem>
                            </FormGridSection>

                            <Divider direction="horizontal" />

                            {birthDate && (
                                <>
                                    <FormGridSection>
                                        <FormGridSectionItem>
                                            <FormGridSectionTitle>{t('patients:medical_info')}</FormGridSectionTitle>
                                        </FormGridSectionItem>
                                        <FormGridSectionItem>
                                            <PatientMedicalInfoFields birthDate={birthDate} />
                                        </FormGridSectionItem>
                                    </FormGridSection>

                                    <Divider direction="horizontal" />

                                    <FormGridSection>
                                        <FormGridSectionItem>
                                            <FormGridSectionTitle>
                                                {t('commons:privacy')}{' '}
                                                <span className="description">({t('commons:optional')})</span>
                                            </FormGridSectionTitle>
                                        </FormGridSectionItem>
                                        <FormGridSectionItem>
                                            <PatientPrivacyFields />
                                        </FormGridSectionItem>
                                    </FormGridSection>

                                    <Divider direction="horizontal" />

                                    <FormGridSection>
                                        <FormGridSectionItem>
                                            <FormGridSectionTitle>
                                                {t('commons:notes')}{' '}
                                                <span className="description">({t('commons:optional')})</span>
                                            </FormGridSectionTitle>
                                        </FormGridSectionItem>
                                        <FormGridSectionItem>
                                            <PatientNotesForm />
                                        </FormGridSectionItem>
                                    </FormGridSection>
                                </>
                            )}

                            {isConfirmationModalVisible && (
                                <CreatePatientConfirmationModal
                                    dialogStore={confirmationDialogStore}
                                    onErrorConfirmationModal={onErrorConfirmationModal}
                                    defaultValues={form.getValues()}
                                />
                            )}
                        </FormProvider>
                    </UnsavedBlocker>
                </ScrollArea>
            </PageContent>
        </PageCardWrapper>
    )
}
